/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useRef,
  useCallback,
} from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { CSSProperties } from 'styled-components';

import { ModalInfo, Input, Button } from '~/components';
import * as S from './styles';
import { light } from '~/styles/themes';

interface ILifesFormModalProps {
  modalIsVisible: boolean;
  setModalIsVisible: Dispatch<SetStateAction<boolean>>;
  initialData?: {
    id?: string;
    quantity?: string;
    credits?: number;
  };
  onSubmit: (data: {
    id?: string;
    quantity: string;
    credits: number;
    isEdit: boolean;
  }) => Promise<void> | void;
  isEdit: boolean;
}

function LifesFormModal({
  modalIsVisible,
  setModalIsVisible,
  initialData,
  onSubmit,
  isEdit,
}: ILifesFormModalProps): ReactElement {
  const formRef = useRef<FormHandles>(null);

  const modalContainerStyle = useRef<CSSProperties>({
    position: 'relative',
    padding: '4.8rem',
  });

  const modalCloseButtonStyle = useRef<CSSProperties>({
    background: 'none',
    position: 'absolute',
    top: '30px',
    left: '30px',
  });

  const inputLabelStyle = useRef<CSSProperties>({ color: '#555' });
  const submitButtonStyle = useRef<CSSProperties>({ borderRadius: '10px' });

  const handleCloseModal = useCallback(() => {
    setModalIsVisible(false);
  }, [setModalIsVisible]);

  const handleSubmit = useCallback(
    async (data: { quantity: string; credits: string; status: string }) => {
      try {
        const schema = Yup.object().shape({
          quantity: Yup.string()
            .required('Quantidade de vidas é obrigatória')
            .min(1, 'Deve ser pelo menos 1'),
          credits: Yup.number()
            .required('Quantidade de créditos é obrigatória')
            .min(0, 'Deve ser pelo menos 0'),
          status: Yup.string().oneOf(['true', 'false']),
        });

        await schema.validate(data, { abortEarly: false });

        await onSubmit({
          id: initialData?.id,
          quantity: data.quantity,
          credits: Number(data.credits),

          isEdit,
        });

        toast.success(
          isEdit ? 'Vidas editadas com sucesso!' : 'Vidas criadas com sucesso!',
        );
        handleCloseModal();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const validationErrors: Record<string, string> = {};
          err.inner.forEach((error: any) => {
            if (error.path) validationErrors[error.path] = error.message;
          });
          formRef.current?.setErrors(validationErrors);
        } else {
          toast.error('Erro ao salvar as vidas');
        }
      }
    },
    [handleCloseModal, onSubmit, initialData, isEdit],
  );

  return (
    <ModalInfo
      modalVisible={modalIsVisible}
      setModalVisible={setModalIsVisible}
      containerStyle={modalContainerStyle.current}
    >
      <button
        type="button"
        onClick={handleCloseModal}
        title="Fechar modal"
        style={modalCloseButtonStyle.current}
      >
        <FiX color={light.colors.primary} size={30} />
      </button>
      <S.SaveOperatorModalContent
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={{
          quantity: initialData?.quantity ?? '',
          credits: initialData?.credits ?? '',
        }}
      >
        <S.SaveOperatorModalInputBox>
          <Input
            labelStyle={inputLabelStyle.current}
            name="quantity"
            id="quantity"
            label="Tipos de vida do lead"
            placeholder="Digite o tipo de vida do lead"
            type="text"
            min={1}
          />
          <Input
            labelStyle={inputLabelStyle.current}
            name="credits"
            id="credits"
            label="Quantidade de créditos"
            placeholder="Digite a quantidade de créditos"
            type="number"
            min={0}
          />

          <S.ButtonGroup>
            <Button type="submit" containerStyle={submitButtonStyle.current}>
              {isEdit ? 'Editar' : 'Salvar'}
            </Button>
          </S.ButtonGroup>
        </S.SaveOperatorModalInputBox>
      </S.SaveOperatorModalContent>
    </ModalInfo>
  );
}

export default LifesFormModal;
